import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrash, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Table, createCustomColumn, createTextColumn, useTableState } from '../deps.js';
import CreateTeamModal from './CreateTeamModal.js';
import TeamDetailModal from './TeamDetailModal.js';
import DeleteTeamModal from './DeleteTeamModal.js';
import BulkDeleteTeamsModal from './BulkDeleteTeamsModal.js';

const useAdminTeams = (directory) => {
  const {
    users,
    teams,
    setTeams,
    roles,
    clinicHeaderOverride,
    teamService,
    reloadTeams,
    addNotification,
    isMaster,
    tableIds,
    adminSelectedClinicId,
  } = directory;

  const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);
  const [teamDetailModalOpen, setTeamDetailModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [deleteTeamModalOpen, setDeleteTeamModalOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [deleteTeamLoading, setDeleteTeamLoading] = useState({});
  const [createTeamLoading, setCreateTeamLoading] = useState(false);
  const [updateTeamLoading, setUpdateTeamLoading] = useState(false);
  const [bulkDeleteTeamsModalOpen, setBulkDeleteTeamsModalOpen] = useState(false);
  const [teamsForBulkDelete, setTeamsForBulkDelete] = useState([]);
  const [bulkDeleteTeamsLoading, setBulkDeleteTeamsLoading] = useState(false);
  const [selectedTeamRows, setSelectedTeamRows] = useState(new Set());

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
  } = useTableState(tableIds.teams);

  const handleCreateTeam = useCallback(async (teamName) => {
    setCreateTeamLoading(true);
    try {
      await teamService.createTeam(teamName, clinicHeaderOverride);
      await reloadTeams();
      setCreateTeamModalOpen(false);
      setCreateTeamLoading(false);
      addNotification(`team-create-${teamName}`, 'Team created successfully');
    } catch (error) {
      console.error('Failed to create team:', error);
      setCreateTeamLoading(false);
      addNotification('team-create-error', 'Failed to create team', { isError: true });
    }
  }, [addNotification, clinicHeaderOverride, reloadTeams, teamService]);

  const handleUpdateTeam = useCallback(async (teamUpdate) => {
    setUpdateTeamLoading(true);
    try {
      await teamService.updateTeam(teamUpdate, clinicHeaderOverride);
      await reloadTeams();
      setTeamDetailModalOpen(false);
      setSelectedTeam(null);
      setUpdateTeamLoading(false);
      addNotification(`team-update-${teamUpdate.teamId}`, 'Team updated successfully');
    } catch (error) {
      console.error('Failed to update team:', error);
      setUpdateTeamLoading(false);
      addNotification('team-update-error', 'Failed to update team', { isError: true });
    }
  }, [addNotification, clinicHeaderOverride, reloadTeams, teamService]);

  const handleDeleteTeamClick = useCallback((team) => {
    setTeamToDelete(team);
    setDeleteTeamModalOpen(true);
  }, []);

  const handleDeleteTeamConfirm = useCallback(() => {
    if (!teamToDelete) return;
    const teamId = teamToDelete.teamId;
    setDeleteTeamLoading((prev) => ({ ...prev, [teamId]: true }));

    teamService.deleteTeam(teamId, clinicHeaderOverride)
      .then(() => {
        setTeams((prevTeams) => prevTeams.filter((t) => t.teamId !== teamId));
        setDeleteTeamLoading((prev) => ({ ...prev, [teamId]: false }));
        setDeleteTeamModalOpen(false);
        setTeamToDelete(null);
        addNotification(`team-delete-${teamId}`, 'Team deleted successfully');
      }).catch(() => {
        console.error(`Failed to delete team ${teamId}:`);
        setDeleteTeamLoading((prev) => ({ ...prev, [teamId]: false }));
        setDeleteTeamModalOpen(false);
        setTeamToDelete(null);
        addNotification(`team-delete-error-${teamId}`, 'Failed to delete team', { isError: true });
      });
  }, [addNotification, clinicHeaderOverride, setTeams, teamService, teamToDelete]);

  const handleBulkDeleteTeams = useCallback((selectedTeamsData) => {
    if (selectedTeamsData.length === 0) return;
    setTeamsForBulkDelete(selectedTeamsData);
    setBulkDeleteTeamsModalOpen(true);
  }, []);

  const confirmBulkDeleteTeams = useCallback(() => {
    if (teamsForBulkDelete.length === 0) return;
    setBulkDeleteTeamsLoading(true);

    const deletePromises = teamsForBulkDelete.map((team) =>
      new Promise((resolve, reject) => {
        teamService.deleteTeam(team.teamId, clinicHeaderOverride)
          .then(() => {
            addNotification(`bulk-delete-team-${team.teamId}`, `${team.teamName} deleted successfully`);
            resolve(team);
          }).catch((error) => {
            console.error(`Failed to delete team ${team.teamId}:`, error);
            addNotification(`bulk-delete-team-error-${team.teamId}`, `Failed to delete ${team.teamName}`, { isError: true });
            reject(error);
          });
      })
    );

    Promise.allSettled(deletePromises).then(() => {
      const deletedTeamIds = teamsForBulkDelete.map((team) => team.teamId);
      setTeams((prevTeams) => prevTeams.filter((team) => !deletedTeamIds.includes(team.teamId)));
      setSelectedTeamRows(new Set());
      setBulkDeleteTeamsModalOpen(false);
      setTeamsForBulkDelete([]);
    }).finally(() => {
      setBulkDeleteTeamsLoading(false);
    });
  }, [addNotification, clinicHeaderOverride, setTeams, teamService, teamsForBulkDelete]);

  const handleTeamRowClick = useCallback((team) => {
    setSelectedTeam(team);
    setTeamDetailModalOpen(true);
  }, []);

  const columns = useMemo(() => [
    createTextColumn(
      'teamName',
      'Team Name',
      (team) => team.teamName
    ),
    createCustomColumn(
      'userCount',
      'Members',
      (team) => team.userIds?.length || 0,
      (value, team) => (
        <span>{team.userIds?.length || 0}</span>
      ),
      {
        sortable: true,
        filterable: false,
      }
    ),
    createCustomColumn(
      'actions',
      'Actions',
      (team) => team,
      (value, team) => (
        <div className="admin-row-actions">
          <button
            className={`admin-button admin-delete-button ${deleteTeamLoading[team.teamId] ? 'admin-disabled-button' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTeamClick(team);
            }}
            disabled={deleteTeamLoading[team.teamId]}
            title="Delete Team"
          >
            {deleteTeamLoading[team.teamId] ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faTrash} />
            )}
          </button>
        </div>
      ),
      {
        sortable: false,
        filterable: false,
        className: 'actions-column',
      }
    ),
  ], [deleteTeamLoading, handleDeleteTeamClick]);

  const bulkActions = useMemo(() => [
    {
      key: 'deleteTeams',
      label: 'Delete Teams',
      icon: faTrash,
      onClick: handleBulkDeleteTeams,
    },
  ], [handleBulkDeleteTeams]);

  const headerActions = isMaster && !adminSelectedClinicId ? null : (
    <button className="admin-add-user-button" onClick={() => setCreateTeamModalOpen(true)}>
      <FontAwesomeIcon icon={faUsers} /> Create Team
    </button>
  );

  const selectedClinicName = isMaster
    ? directory.clinics.find((c) => c.clinicId === adminSelectedClinicId)?.clinicName || ''
    : '';

  const section = (
    <div className="admin-table-section">
      <h2 className="admin-table-title">
        Teams{isMaster && adminSelectedClinicId ? ` — ${selectedClinicName}` : ''}
      </h2>
      <Table
        id={tableIds.teams}
        data={teams}
        columns={columns}
        getRowKey={(team) => team.teamId}
        sortState={sortState}
        onSortChange={setSortState}
        filterValues={filterValues}
        onFilterChange={setFilterValues}
        disabledFilters={disabledFilters}
        onDisabledFiltersChange={setDisabledFilters}
        paginationState={paginationState}
        onPaginationChange={setPaginationState}
        enableSelection={true}
        bulkActions={bulkActions}
        selectedRows={selectedTeamRows}
        onSelectionChange={setSelectedTeamRows}
        onRowClick={handleTeamRowClick}
        emptyMessage="No teams found."
      />
    </div>
  );

  const modals = (
    <>
      <CreateTeamModal
        isOpen={createTeamModalOpen}
        onClose={() => setCreateTeamModalOpen(false)}
        onSubmit={handleCreateTeam}
        loading={createTeamLoading}
      />
      <TeamDetailModal
        isOpen={teamDetailModalOpen}
        onClose={() => {
          setTeamDetailModalOpen(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        users={users}
        roles={roles}
        onUpdate={handleUpdateTeam}
        loading={updateTeamLoading}
      />
      <DeleteTeamModal
        isOpen={deleteTeamModalOpen}
        onClose={() => {
          setDeleteTeamModalOpen(false);
          setTeamToDelete(null);
        }}
        onConfirm={handleDeleteTeamConfirm}
        team={teamToDelete}
        loading={deleteTeamLoading[teamToDelete?.teamId]}
      />
      <BulkDeleteTeamsModal
        isOpen={bulkDeleteTeamsModalOpen}
        onClose={() => {
          setBulkDeleteTeamsModalOpen(false);
          setTeamsForBulkDelete([]);
        }}
        onConfirm={confirmBulkDeleteTeams}
        teams={teamsForBulkDelete}
        loading={bulkDeleteTeamsLoading}
      />
    </>
  );

  return { headerActions, section, modals };
};

export default useAdminTeams;
